<template>
	<div class="w-full">
		<!-- Header -->
		<div class="mb-6">
			<h1
				class="text-2xl lg:text-3xl font-bold text-text-primary mb-2 tracking-tight"
			>
				Insurance Quotations
			</h1>
			<p class="text-sm lg:text-base text-text-secondary">
				Manage and track your insurance quotations
			</p>
		</div>

		<!-- Quotations Grid -->
		<div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
			<div
				v-for="quotation in selectQuotationsData"
				:key="quotation._id"
				class="bg-white border border-border rounded-lg p-5 hover:shadow-md transition-all duration-300 group"
			>
				<!-- Header Section -->
				<div class="mb-4">
					<div class="flex items-start justify-between mb-3">
						<h2
							class="text-base lg:text-lg font-semibold text-text-primary line-clamp-2 flex-1 mr-2"
						>
							{{ quotation.quotationDescription }}
						</h2>
						<div class="flex-shrink-0">
							<span
								class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap"
								:class="{
									'bg-accent/10 text-accent':
										quotation.quotationPayment
											.paymentStatus === 'processing',
									'bg-green-50 text-green-700':
										quotation.quotationPayment
											.paymentStatus === 'paid',
									'bg-red-50 text-red-700':
										quotation.quotationPayment
											.paymentStatus === 'pending',
								}"
							>
								<span
									class="w-1.5 h-1.5 rounded-full mr-1.5"
									:class="{
										'bg-accent':
											quotation.quotationPayment
												.paymentStatus === 'processing',
										'bg-green-500':
											quotation.quotationPayment
												.paymentStatus === 'paid',
										'bg-red-500':
											quotation.quotationPayment
												.paymentStatus === 'pending',
									}"
								></span>
								{{
									quotation.quotationPayment.paymentStatus
										.charAt(0)
										.toUpperCase() +
									quotation.quotationPayment.paymentStatus.slice(
										1,
									)
								}}
							</span>
						</div>
					</div>

					<!-- Client Info -->
					<div
						class="flex items-center text-xs lg:text-sm text-text-secondary mb-2"
					>
						<svg
							class="w-3 h-3 lg:w-4 lg:h-4 mr-1.5 text-text-secondary/60"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
							/>
						</svg>
						<span class="font-medium text-text-primary">{{
							quotation.clientDetails.names.fname
						}}</span>
						<span class="mx-1">•</span>
						<span class="truncate">{{
							quotation.clientDetails.email
						}}</span>
					</div>

					<!-- Vehicle Info -->
					<div
						v-if="quotation.risks.lenth > 0"
						class="flex items-center text-xs lg:text-sm text-text-secondary"
					>
						<svg
							class="w-3 h-3 lg:w-4 lg:h-4 mr-1.5 text-text-secondary/60"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
							/>
						</svg>
						<span class="font-medium text-text-primary">{{
							quotation.risks[0].riskRef
						}}</span>
						<span class="mx-1">•</span>
						<span
							>KES
							{{
								quotation.risks[0].value.toLocaleString()
							}}</span
						>
					</div>
				</div>

				<!-- Premium Section -->
				<div
					class="bg-primary/5 rounded-lg p-4 mb-4 border border-primary/10"
				>
					<div class="flex items-center justify-between mb-2">
						<span
							class="text-xs lg:text-sm font-medium text-text-secondary"
							>Total Premium</span
						>
						<span
							class="text-lg lg:text-2xl font-bold text-primary"
						>
							KES
							{{
								quotation.quotationPremium.totalPremium.toLocaleString()
							}}
						</span>
					</div>
					<div
						class="flex items-center justify-between text-xs text-text-secondary gap-2"
					>
						<span class="font-semibold"
							>Base: KES
							{{
								quotation.quotationPremium.basePremium.toLocaleString()
							}}</span
						>
						<span class="font-semibold"
							>Charges: KES
							{{
								quotation.quotationPremium.totalCharges.toLocaleString()
							}}</span
						>
					</div>
				</div>

				<!-- Payment Section -->
				<div
					v-if="
						quotation.quotationPayment.paymentStatus === 'pending'
					"
					class="space-y-3"
				>
					<div class="border-t border-border pt-3">
						<h3
							class="text-xs lg:text-sm font-semibold text-text-primary mb-3 flex items-center"
						>
							<svg
								class="w-3 h-3 lg:w-4 lg:h-4 mr-1.5 text-primary"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
								/>
							</svg>
							Complete Payment
						</h3>

						<div class="space-y-2.5">
							<div>
								<label
									:for="'phoneNumber-' + quotation._id"
									class="block text-xs font-medium text-text-primary mb-1"
								>
									M-Pesa Phone Number
								</label>
								<div class="relative">
									<div
										class="absolute inset-y-0 left-0 flex items-center pl-2.5"
									>
										<svg
											class="w-4 h-4 text-text-secondary"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												stroke-linecap="round"
												stroke-linejoin="round"
												stroke-width="2"
												d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
											/>
										</svg>
									</div>
									<input
										:id="'phoneNumber-' + quotation._id"
										type="tel"
										v-model="
											phoneNumbers[
												quotation.systemQuotationRef
											]
										"
										required
										placeholder="7XXXXXXXX"
										class="block w-full pl-9 pr-9 py-2 text-xs lg:text-sm border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-all duration-200 placeholder-text-secondary/50 bg-surface"
										:class="{
											'border-red-300 focus:border-red-500 focus:ring-red-500':
												phoneErrors[
													quotation.systemQuotationRef
												],
											'border-border focus:border-primary focus:ring-primary':
												!phoneErrors[
													quotation.systemQuotationRef
												] ||
												!phoneNumbers[
													quotation.systemQuotationRef
												],
											'border-green-300':
												phoneNumbers[
													quotation.systemQuotationRef
												] &&
												!phoneErrors[
													quotation.systemQuotationRef
												],
										}"
										@input="
											validatePhoneNumber(
												quotation.systemQuotationRef,
											)
										"
									/>
									<div
										v-if="
											phoneNumbers[
												quotation.systemQuotationRef
											] &&
											!phoneErrors[
												quotation.systemQuotationRef
											]
										"
										class="absolute inset-y-0 right-0 flex items-center pr-2.5"
									>
										<svg
											class="w-4 h-4 text-green-500"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												stroke-linecap="round"
												stroke-linejoin="round"
												stroke-width="2"
												d="M5 13l4 4L19 7"
											/>
										</svg>
									</div>
								</div>
								<p
									v-if="
										phoneErrors[
											quotation.systemQuotationRef
										]
									"
									class="text-red-600 text-xs mt-1 flex items-center"
								>
									<svg
										class="w-3 h-3 mr-1 flex-shrink-0"
										fill="currentColor"
										viewBox="0 0 20 20"
									>
										<path
											fill-rule="evenodd"
											d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
											clip-rule="evenodd"
										/>
									</svg>
									{{
										phoneErrors[
											quotation.systemQuotationRef
										]
									}}
								</p>
							</div>

							<button
								class="w-full inline-flex items-center justify-center px-3 py-2 text-xs lg:text-sm font-semibold rounded-lg text-white bg-primary hover:bg-primary-light focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-primary shadow-sm hover:shadow-md active:scale-95"
								:disabled="
									!isValidPhoneNumber(
										quotation.systemQuotationRef,
									) || loading[quotation.systemQuotationRef]
								"
								@click="retryPayment(quotation)"
							>
								<div
									v-if="loading[quotation.systemQuotationRef]"
									class="flex items-center"
								>
									<svg
										class="animate-spin -ml-0.5 mr-1.5 h-3.5 w-3.5 text-white"
										fill="none"
										viewBox="0 0 24 24"
									>
										<circle
											class="opacity-25"
											cx="12"
											cy="12"
											r="10"
											stroke="currentColor"
											stroke-width="4"
										></circle>
										<path
											class="opacity-75"
											fill="currentColor"
											d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
										></path>
									</svg>
									Processing...
								</div>
								<div v-else class="flex items-center">
									<svg
										class="w-3.5 h-3.5 lg:w-4 lg:h-4 mr-1.5"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											stroke-linecap="round"
											stroke-linejoin="round"
											stroke-width="2"
											d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"
										/>
									</svg>
									Pay with M-Pesa
								</div>
							</button>
						</div>
					</div>
				</div>

				<!-- Success State for Paid Quotations -->
				<div
					v-else-if="
						quotation.quotationPayment.paymentStatus === 'paid'
					"
					class="border-t border-border pt-3"
				>
					<div
						class="flex items-center text-green-600 bg-green-50 px-3 py-2.5 rounded-lg"
					>
						<svg
							class="w-4 h-4 mr-2 flex-shrink-0"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
							/>
						</svg>
						<span class="font-semibold text-xs lg:text-sm"
							>Payment Completed</span
						>
					</div>
				</div>

				<!-- Processing State -->
				<div
					v-else-if="
						quotation.quotationPayment.paymentStatus ===
						'processing'
					"
					class="border-t border-border pt-3 space-y-2"
				>
					<div
						class="flex items-center text-accent bg-accent/5 px-3 py-2.5 rounded-lg"
					>
						<svg
							class="w-4 h-4 mr-2 flex-shrink-0 animate-spin"
							fill="none"
							viewBox="0 0 24 24"
						>
							<circle
								class="opacity-25"
								cx="12"
								cy="12"
								r="10"
								stroke="currentColor"
								stroke-width="4"
							></circle>
							<path
								class="opacity-75"
								fill="currentColor"
								d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
							></path>
						</svg>
						<span class="font-semibold text-xs lg:text-sm"
							>Processing Payment...</span
						>
					</div>

					<button
						@click="CompletePayment(quotation)"
						:disabled="loading[quotation.systemQuotationRef]"
						class="w-full inline-flex items-center justify-center px-3 py-2 text-xs lg:text-sm font-semibold rounded-lg text-white bg-brand-cyan hover:bg-brand-cyan/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-cyan transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-brand-cyan shadow-sm hover:shadow-md active:scale-95"
					>
						<span
							v-if="loading[quotation.systemQuotationRef]"
							class="flex items-center"
						>
							<svg
								class="animate-spin -ml-0.5 mr-1.5 h-3.5 w-3.5 text-white"
								fill="none"
								viewBox="0 0 24 24"
							>
								<circle
									class="opacity-25"
									cx="12"
									cy="12"
									r="10"
									stroke="currentColor"
									stroke-width="4"
								></circle>
								<path
									class="opacity-75"
									fill="currentColor"
									d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
								></path>
							</svg>
							Processing...
						</span>
						<span v-else class="flex items-center">
							<svg
								class="w-3.5 h-3.5 lg:w-4 lg:h-4 mr-1.5"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M5 13l4 4L19 7"
								/>
							</svg>
							Complete Payment
						</span>
					</button>
				</div>
			</div>
		</div>

		<!-- Empty State -->
		<div
			v-if="!selectQuotationsData || selectQuotationsData.length === 0"
			class="text-center py-12 bg-white border border-border rounded-lg"
		>
			<div
				class="inline-flex items-center justify-center w-14 h-14 rounded-lg bg-surface mb-3"
			>
				<svg
					class="w-7 h-7 text-text-secondary"
					fill="none"
					viewBox="0 0 24 24"
					stroke="currentColor"
				>
					<path
						stroke-linecap="round"
						stroke-linejoin="round"
						stroke-width="2"
						d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
					/>
				</svg>
			</div>
			<h3
				class="text-base lg:text-lg font-semibold text-text-primary mb-1"
			>
				No quotations found
			</h3>
			<p class="text-xs lg:text-sm text-text-secondary mb-4">
				Get started by creating your first insurance quotation.
			</p>
			<button
				class="inline-flex items-center px-4 py-2 text-xs lg:text-sm font-semibold text-white bg-primary hover:bg-primary-light rounded-lg transition-all duration-200 shadow-sm hover:shadow-md active:scale-95"
			>
				<svg
					class="w-4 h-4 mr-1.5"
					fill="none"
					stroke="currentColor"
					viewBox="0 0 24 24"
				>
					<path
						stroke-linecap="round"
						stroke-linejoin="round"
						stroke-width="2"
						d="M12 4v16m8-8H4"
					/>
				</svg>
				Create Quotation
			</button>
		</div>

		<!-- Pagination -->
		<div
			v-if="selectQuotationsData && selectQuotationsData.length > 0"
			class="mt-6"
		>
			<Pagination @page-change="fetchClientQuotations" />
		</div>
	</div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { FetchClientQuotations, ManualPayment } from '../api/portal';
import { RequestPayment } from '../../insurance/api/insurance';
import { getPageNumbers } from '../../../helpers/pagination';

export default {
	data() {
		return {
			phoneNumbers: {},
			phoneErrors: {},
			loading: {},
		};
	},
	components: {
		Pagination: () => import('../templates/others/pagination.vue'),
	},
	computed: {
		...mapGetters('quotations', ['selectQuotationsData']),
		...mapGetters('pagination', ['currentPage']),
	},
	methods: {
		...mapActions(['initialize']),
		...mapActions('quotations', [
			'setClientQuotationData',
			'setClientPoliciesData',
		]),
		...mapActions('pagination', [
			'setPagesArray',
			'setTotalPages',
			'setCurrentPage',
		]),

		async fetchClientQuotations() {
			try {
				const fetchData = {
					querryOptions: {
						page: this.currentPage,
					},
				};
				const res = await FetchClientQuotations(fetchData);
				if (res.status) {
					this.setClientQuotationData(res.data[0].items);
					const pages = getPageNumbers(res.data[0].pageCount, 20);
					this.setPagesArray(pages);
					this.setTotalPages(res.data[0].totalCount);
					this.$emit('notifications:show', {
						type: 'success',
						title: 'Success',
						message: 'Quotations fetched successfully',
						duration: 3000,
					});
				}
			} catch (err) {
				console.error('Error fetching quotations:', err);
				this.$emit('notifications:show', {
					type: 'error',
					title: 'Error',
					message: 'Failed to fetch quotations',
					duration: 3000,
				});
			}
		},
		validatePhoneNumber(systemQuotationRef) {
			const phone = this.phoneNumbers[systemQuotationRef] || '';
			const kenyaPhoneRegex = /^(7|1)\d{8}$/;
			if (!phone) {
				this.$set(
					this.phoneErrors,
					systemQuotationRef,
					'Phone number is required',
				);
				return false;
			} else if (!kenyaPhoneRegex.test(phone)) {
				this.$set(
					this.phoneErrors,
					systemQuotationRef,
					'Please enter a valid Kenyan phone number (e.g.,7XXXXXXXX)',
				);
				return false;
			} else {
				this.$set(this.phoneErrors, systemQuotationRef, '');
				return true;
			}
		},
		isValidPhoneNumber(systemQuotationRef) {
			return this.validatePhoneNumber(systemQuotationRef);
		},
		async retryPayment(quotation) {
			const invoiceSystemRef =
				quotation.quotationPayment.invoiceSystemRef;
			const systemQuotationRef = quotation.systemQuotationRef;
			if (!this.isValidPhoneNumber(systemQuotationRef)) return;

			const quotationData = {
				invoiceSystemRef: invoiceSystemRef,
				invoicePaymentDetails: {
					paymentProvider: 'mpesa',
					params: {
						PhoneNumber:
							'254' + this.phoneNumbers[systemQuotationRef],
					},
				},
			};

			this.$set(this.loading, systemQuotationRef, true);

			try {
				const res = await RequestPayment(quotationData);
				if (res.status) {
					this.$emit('notifications:show', {
						type: 'success',
						title: 'Success',
						message: 'Payment request sent successfully',
						duration: 3000,
					});
					await this.fetchClientQuotations();
				} else {
					throw new Error('Payment request failed');
				}
			} catch (err) {
				console.error('Error retrying payment:', err);
				this.$emit('notifications:show', {
					type: 'error',
					title: 'Error',
					message: err.message || 'Failed to process payment',
					duration: 3000,
				});
			} finally {
				this.$set(this.loading, systemQuotationRef, false);
			}
		},
		async CompletePayment(quotation) {
			const systemQuotationRef =
				quotation && quotation.systemQuotationRef;
			if (!systemQuotationRef) {
				console.error('Missing systemQuotationRef in quotation');
				return;
			}

			const quotationData = {
				systemQuotationRef: systemQuotationRef,
				QuotationPaymentDetails: {
					paymentProvider: 'mpesa',
					providerPaymentRef:
						quotation && quotation.quotationPayment
							? quotation.quotationPayment.paymentRef
							: '',
					paidBy: 'TEST',
					paymentRemarks: 'TEST',
					paymentAmount:
						quotation && quotation.quotationPremium
							? quotation.quotationPremium.totalPremium
							: 0,
				},
			};

			this.$set(this.loading, systemQuotationRef, true);
			console.log('Submitting payment...', quotationData);

			try {
				const res = await ManualPayment(quotationData);

				if (res && res.status) {
					this.$emit('notifications:show', {
						type: 'success',
						title: 'Success',
						message: 'Payment completed successfully',
						duration: 3000,
					});

					await this.fetchClientQuotations();
				} else {
					throw new Error(
						(res && res.message) || 'Payment request failed',
					);
				}
			} catch (err) {
				console.error('Error processing payment:', err);
				this.$emit('notifications:show', {
					type: 'error',
					title: 'Error',
					message: err.message || 'Failed to process payment',
					duration: 3000,
				});
			} finally {
				this.$set(this.loading, systemQuotationRef, false);
			}
		},
	},
	async mounted() {
		this.setCurrentPage(1);
		await this.fetchClientQuotations();
		console.log('quotationsData', this.selectQuotationsData);
	},
};
</script>

<style scoped>
.line-clamp-2 {
	display: -webkit-box;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
	overflow: hidden;
}

/* Focus styles for accessibility */
button:focus-visible,
input:focus-visible {
	outline: 2px solid theme('colors.primary.DEFAULT');
	outline-offset: 2px;
}

/* Responsive adjustments */
@media (max-width: 768px) {
	.text-3xl {
		font-size: 1.875rem;
		line-height: 2.25rem;
	}
}
</style>
